import React, { forwardRef, useContext, useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import { ContextData } from '../../context/Context';
import baseUrl from '../../BaseURL';

export function privateImageUrl(source) {
  if (!source) return null;
  try {
    const url = new URL(source, baseUrl);
    const api = new URL(baseUrl);
    return url.origin === api.origin && /^\/files\/assets\/MemberPictures\/[^/]+$/.test(url.pathname) ? url.href : null;
  } catch { return null; }
}

// Fetch private images with the same in-memory credentials as API requests.
// Object URLs stay in memory and are revoked when credentials change or the
// component unmounts. Never put credentials in image URLs or browser storage.
export function usePrivateImage(source) {
  const context = useContext(ContextData);
  const token = context?.accesstoken;
  const url = privateImageUrl(source);
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!url || !token) return;
    const controller = new AbortController();
    let objectUrl;
    let active = true;
    async function loadImage() {
      const response = await fetch(url, { credentials: 'include', headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
      if (!response.ok) throw new Error('Image access was refused.');
      const blob = await response.blob();
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setImage({ source, token, url: objectUrl });
    }
    loadImage().catch(() => { if (active) setImage({ source, token, url: undefined }); });
    return () => { active = false; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url, source, token]);
  if (!url) return source;
  return token && image?.source === source && image?.token === token ? image.url : undefined;
}

const PrivateImage = forwardRef(function PrivateImage({ src, alt = '', ...props }, ref) {
  const resolved = usePrivateImage(src);
  return <img {...props} ref={ref} src={resolved} alt={alt} data-private-image={privateImageUrl(src) ? 'true' : undefined} aria-busy={Boolean(src && !resolved)} />;
});
export function PrivateAvatar({ src, ...props }) {
  return <Avatar {...props} src={usePrivateImage(src)} />;
}
export function PrivateImageLink({ src, alt, className, children }) {
  const resolved = usePrivateImage(src);
  return <a href={resolved} target="_blank" rel="noreferrer" aria-disabled={!resolved} onClick={event => { if (!resolved) event.preventDefault(); }}><img className={className} src={resolved} alt={alt} data-private-image="true" aria-busy={Boolean(src && !resolved)} />{children}</a>;
}
export default PrivateImage;
